/**
 * GlossaryModal
 *
 * Full-screen modal listing all Portfolio glossary terms, grouped by category.
 * Layout:
 *   - Sticky header with title + close button + print button
 *   - Scrollable body: category sections with hairline dividers
 *   - Each term: color dot + label (semibold) + definition + threshold + action
 *   - Uppercase overlines, editorial hairline rules
 *
 * Trigger: "?" icon button in the Portfolio page header (rendered externally).
 * Mobile: full-screen. Desktop: centered modal max-w-2xl.
 *
 * Print: clicking the Print button adds `printing-glossary` to <html>,
 * calls window.print(), then removes it via the afterprint event.
 * A dedicated #glossary-print-doc is portaled to document.body - a simple
 * linear document hidden on screen, shown only under html.printing-glossary
 * in @media print. This avoids all fragile visibility/flatten tricks on the
 * interactive modal's flex/overflow/sticky structure.
 */

import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  PORTFOLIO_GLOSSARY,
  GLOSSARY_CATEGORY_ORDER,
  GLOSSARY_CATEGORY_LABELS,
  type GlossaryCategory,
} from '@/constants/portfolioGlossary';
import { Tooltip } from '@/components/ui/Tooltip';
import { useTimezone } from '@/contexts/TimezoneContext';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

interface GlossaryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const Overline = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mb-0.5">
    {children}
  </p>
);

// ── Main component ────────────────────────────────────────────────────────────

export const GlossaryModal = ({ isOpen, onClose }: GlossaryModalProps) => {
  const { t } = useTranslation('agency');
  const panelRef = useRef<HTMLDivElement>(null);
  const { formatDateOnly } = useTimezone();

  // ESC to close + body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', handleKey);
    const unlockScroll = lockBodyScroll();

    return () => {
      document.removeEventListener('keydown', handleKey);
      unlockScroll();
    };
  }, [isOpen, onClose]);

  // Print handler: toggle `printing-glossary` class on <html> around window.print()
  // The afterprint event cleans up even if the user cancels the dialog.
  const handlePrint = useCallback(() => {
    const html = document.documentElement;
    html.classList.add('printing-glossary');

    const cleanup = () => {
      html.classList.remove('printing-glossary');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

    window.print();
  }, []);

  if (!isOpen) return null;

  // Group terms by category, maintaining GLOSSARY_CATEGORY_ORDER
  const termsByCategory = GLOSSARY_CATEGORY_ORDER.reduce<
    Record<GlossaryCategory, typeof PORTFOLIO_GLOSSARY[string][]>
  >((acc, cat) => {
    acc[cat] = Object.values(PORTFOLIO_GLOSSARY).filter((t) => t.category === cat);
    return acc;
  }, {} as Record<GlossaryCategory, typeof PORTFOLIO_GLOSSARY[string][]>);

  return (
    <>
      {/* ── Interactive screen modal - UNCHANGED from original ───────────────── */}
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="glossary-modal-title"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Panel */}
        <div
          ref={panelRef}
          className="relative bg-white flex flex-col w-full max-h-[95dvh] rounded-t-2xl sm:rounded-2xl sm:w-[calc(100vw-2rem)] sm:max-w-2xl sm:max-h-[calc(100vh-4rem)] shadow-2xl overflow-hidden"
        >
          {/* Mobile drag handle */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden shrink-0">
            <div className="w-8 h-1 rounded-full bg-gray-300" aria-hidden="true" />
          </div>

          {/* Sticky header */}
          <div className="shrink-0 px-5 sm:px-6 py-4 border-b border-gray-900/10 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium mb-0.5">
                {t('glossary_modal_overline', { defaultValue: 'Portfolio reference' })}
              </p>
              <h2
                id="glossary-modal-title"
                className="font-wagner text-2xl leading-tight tracking-tight text-gray-900"
              >
                {t('glossary_modal_title', { defaultValue: 'Brand Portfolio glossary' })}
              </h2>
            </div>

            {/* Print button */}
            <Tooltip
              content={t('glossary_print_tooltip', { defaultValue: 'Print glossary (or save as PDF)' })}
              side="bottom"
            >
              <button
                type="button"
                onClick={handlePrint}
                className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
                aria-label={t('glossary_print_aria', { defaultValue: 'Print glossary (or save as PDF)' })}
              >
                <i className="hm hm-printer" style={{ fontSize: 16 }} aria-hidden="true" />
              </button>
            </Tooltip>

            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
              aria-label={t('glossary_modal_close', { defaultValue: 'Close glossary' })}
            >
              <i className="hm hm-cross" style={{ fontSize: 16 }} aria-hidden="true" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-10">
            {GLOSSARY_CATEGORY_ORDER.map((cat) => {
              const terms = termsByCategory[cat];
              if (!terms || terms.length === 0) return null;

              return (
                <section key={cat} aria-labelledby={`glossary-cat-${cat}`} className="pt-6 first:pt-0">
                  {/* Category heading */}
                  <div className="flex items-center gap-3 mb-5">
                    <h3
                      id={`glossary-cat-${cat}`}
                      className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500 shrink-0"
                    >
                      {t(`glossary_category_${cat.replace(/-/g, '_')}`, {
                        defaultValue: GLOSSARY_CATEGORY_LABELS[cat],
                      })}
                    </h3>
                    <div className="flex-1 border-t border-gray-200" aria-hidden="true" />
                  </div>

                  {/* Terms */}
                  <div className="space-y-7">
                    {terms.map((term) => {
                      const label = t(term.labelKey, { defaultValue: term.labelFallback });
                      const definition = t(term.definitionKey, { defaultValue: term.definitionFallback });
                      const threshold = term.thresholdKey
                        ? t(term.thresholdKey, { defaultValue: term.thresholdFallback ?? '' })
                        : term.thresholdFallback;
                      const action = term.actionKey
                        ? t(term.actionKey, { defaultValue: term.actionFallback ?? '' })
                        : term.actionFallback;

                      return (
                        <div key={term.id} className="flex items-start gap-3">
                          {/* Color dot */}
                          {term.dotColor && (
                            <span
                              className={`w-2 h-2 rounded-full mt-1 shrink-0 ${term.dotColor}`}
                              aria-hidden="true"
                            />
                          )}

                          <div className="flex-1 min-w-0">
                            {/* Term label */}
                            <p className="text-sm font-semibold text-gray-900 leading-tight mb-1.5">
                              {label}
                            </p>

                            {/* Definition */}
                            <div className="mb-1.5">
                              <Overline>
                                {t('glossary_overline_what', { defaultValue: 'What' })}
                              </Overline>
                              <p className="text-xs text-gray-700 leading-relaxed">{definition}</p>
                            </div>

                            {/* Threshold */}
                            {threshold && (
                              <div className="mb-1.5">
                                <Overline>
                                  {t('glossary_overline_when', { defaultValue: 'Triggered when' })}
                                </Overline>
                                <p className="text-xs font-medium text-gray-900 leading-relaxed">
                                  {threshold}
                                </p>
                              </div>
                            )}

                            {/* Action */}
                            {action && (
                              <div>
                                <Overline>
                                  {t('glossary_overline_action', { defaultValue: 'What to do' })}
                                </Overline>
                                <p className="text-xs text-primary-700 leading-relaxed">{action}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}

            {/* Bottom breathing room */}
            <div className="h-4" />
          </div>
        </div>
      </div>

      {/* ── Print-only document - portaled to <body> so it escapes #root ─────── */}
      {createPortal(
        <div id="glossary-print-doc" aria-hidden="true">
          <header className="glossary-print-doc-header">
            <h1>{t('glossary_print_title', { defaultValue: 'Ägile Ässets - Brand Portfolio Glossary' })}</h1>
            <p className="glossary-print-doc-date">{formatDateOnly(new Date().toISOString())}</p>
          </header>
          {GLOSSARY_CATEGORY_ORDER.map((cat) => {
            const terms = termsByCategory[cat];
            if (!terms || terms.length === 0) return null;
            return (
              <section key={cat} className="glossary-print-doc-category">
                <h2 className="glossary-print-doc-category-title">
                  {t(`glossary_category_${cat.replace(/-/g, '_')}`, {
                    defaultValue: GLOSSARY_CATEGORY_LABELS[cat],
                  })}
                </h2>
                {terms.map((term) => {
                  const label = t(term.labelKey, { defaultValue: term.labelFallback });
                  const definition = t(term.definitionKey, { defaultValue: term.definitionFallback });
                  const threshold = term.thresholdKey
                    ? t(term.thresholdKey, { defaultValue: term.thresholdFallback ?? '' })
                    : term.thresholdFallback;
                  const action = term.actionKey
                    ? t(term.actionKey, { defaultValue: term.actionFallback ?? '' })
                    : term.actionFallback;

                  return (
                    <article key={term.id} className="glossary-print-doc-term">
                      <h3 className="glossary-print-doc-term-label">{label}</h3>
                      <p className="glossary-print-doc-term-field">
                        <strong>{t('glossary_overline_what', { defaultValue: 'What' })}:</strong>{' '}
                        {definition}
                      </p>
                      {threshold && (
                        <p className="glossary-print-doc-term-field">
                          <strong>
                            {t('glossary_overline_when', { defaultValue: 'Triggered when' })}:
                          </strong>{' '}
                          {threshold}
                        </p>
                      )}
                      {action && (
                        <p className="glossary-print-doc-term-field">
                          <strong>
                            {t('glossary_overline_action', { defaultValue: 'What to do' })}:
                          </strong>{' '}
                          {action}
                        </p>
                      )}
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
};

export default GlossaryModal;
